import { Grid } from '@mui/material';
import { OptimizerConfig, TrafficOptions, TruckOptions } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import lodash from 'lodash';
import { useEffect, useState } from 'react';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import { HeaderContent } from '../../components/HeaderContent';
import { Project } from '../../domain/project/Project';
import { useUpdateProject } from '../../hooks/project/useUpdateProject';
import { useAsUser } from '../../hooks/session/useAsUser';
import { useRetrieveSupportDocs } from '../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import Button from '../../react-components/Button';
import OptimizerConfigComponent from '../shared/optimizer-config/OptimizerConfigComponent';
import { TrafficOptionsComponent } from '../shared/optimizer-config/TrafficOptionsComponent';

type ProjectSettingsOptimizerProps = {
  project: Project;
  isDirtyState: (...args: any[]) => any;
};

export const ProjectSettingsOptimizer = ({ project, isDirtyState }: ProjectSettingsOptimizerProps) => {
  const { data: user } = useRetrieveUser();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { run: editProject, success: editSuccess } = useUpdateProject({ project });
  const { asUser } = useAsUser();
  const [dirtyState, setDirtyState] = useState<boolean>(false);
  const [optimizerConfig, setOptimizerConfig] = useState<Record<string, any>>({
    max_wait_time: project.optimizer_config?.max_wait_time,
    first_solution_strategy: project.optimizer_config?.first_solution_strategy,
    local_search_strategy: project.optimizer_config?.local_search_strategy,
    time_limit_seconds: project.optimizer_config?.time_limit_seconds,
    lns_time_limit: project.optimizer_config?.lns_time_limit,
    balance_services: project.optimizer_config?.balance_services,
    service_duration: project.optimizer_config?.service_duration,
    time_span_coefficient: project.optimizer_config?.time_span_coefficient,
    distance_span_coefficient: project.optimizer_config?.distance_span_coefficient,
    use_distance: project.optimizer_config?.use_distance,
    drop_penalty: project.optimizer_config?.drop_penalty,
    distance_balance: project.optimizer_config?.distance_balance,
    isolation_factor: project.optimizer_config?.isolation_factor,
    stop_grouping_distance: project.optimizer_config?.stop_grouping_distance,
  });
  const [trafficOptions, setTrafficOptions] = useState<Record<string, any>>({
    multiplier: project.traffic_options?.multiplier,
    mode: project.traffic_options?.mode,
    ...(project.traffic_options?.truck
      ? {
          truck: {
            shipped_hazardous_goods: project.traffic_options?.truck?.shipped_hazardous_goods,
            gross_weight: project.traffic_options?.truck?.gross_weight,
            height: project.traffic_options?.truck?.height,
            width: project.traffic_options?.truck?.width,
            length: project.traffic_options?.truck?.length,
            tunnel_category: project.traffic_options?.truck?.tunnel_category,
            axle_count: project.traffic_options?.truck?.axle_count,
            type: project.traffic_options?.truck?.type,
            trailer_count: project.traffic_options?.truck?.trailer_count,
          },
        }
      : {}),
    avoid: project.traffic_options?.avoid,
    // departure_at: project.traffic_options?.departure_at,
  });

  useEffect(() => {
    if (project) {
      setOptimizerConfig({
        max_wait_time: project.optimizer_config?.max_wait_time,
        first_solution_strategy: project.optimizer_config?.first_solution_strategy,
        local_search_strategy: project.optimizer_config?.local_search_strategy,
        time_limit_seconds: project.optimizer_config?.time_limit_seconds,
        lns_time_limit: project.optimizer_config?.lns_time_limit,
        balance_services: project.optimizer_config?.balance_services,
        service_duration: project.optimizer_config?.service_duration,
        time_span_coefficient: project.optimizer_config?.time_span_coefficient,
        distance_span_coefficient: project.optimizer_config?.distance_span_coefficient,
        use_distance: project.optimizer_config?.use_distance,
        drop_penalty: project.optimizer_config?.drop_penalty,
        distance_balance: project.optimizer_config?.distance_balance,
        isolation_factor: project.optimizer_config?.isolation_factor,
        stop_grouping_distance: project.optimizer_config?.stop_grouping_distance,
      });

      setTrafficOptions({
        multiplier: project.traffic_options?.multiplier,
        mode: project.traffic_options?.mode,
        ...(project.traffic_options?.truck
          ? {
              truck: {
                shipped_hazardous_goods: project.traffic_options?.truck?.shipped_hazardous_goods,
                gross_weight: project.traffic_options?.truck?.gross_weight,
                height: project.traffic_options?.truck?.height,
                width: project.traffic_options?.truck?.width,
                length: project.traffic_options?.truck?.length,
                tunnel_category: project.traffic_options?.truck?.tunnel_category,
                axle_count: project.traffic_options?.truck?.axle_count,
                type: project.traffic_options?.truck?.type,
                trailer_count: project.traffic_options?.truck?.trailer_count,
              },
            }
          : {}),
        avoid: project.traffic_options?.avoid,
        // departure_at: project.traffic_options?.departure_at,
      });
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      if (
        !lodash.isEqual(optimizerConfig, project.optimizer_config ? project.optimizer_config.getValue() : undefined) ||
        !lodash.isEqual(
          removeEmpty(trafficOptions),
          project.traffic_options
            ? removeEmpty({
                ...project.traffic_options.getValue(),
                ...(project.traffic_options.truck
                  ? {
                      truck: project.traffic_options.truck.getValue(),
                    }
                  : {}),
              })
            : undefined
        )
      ) {
        setDirtyState(true);
      } else {
        setDirtyState(false);
      }
    }
  }, [editSuccess, project, optimizerConfig, trafficOptions]);

  useEffect(() => {
    isDirtyState(dirtyState);
  }, [dirtyState, isDirtyState]);

  const handleEditProject = () => {
    if (dirtyState) {
      editProject({
        ...(optimizerConfig ? { optimizer_config: OptimizerConfig.create(optimizerConfig) } : {}),
        ...(trafficOptions
          ? {
              traffic_options: TrafficOptions.create({
                ...trafficOptions,
                ...(trafficOptions.truck ? { truck: TruckOptions.create(trafficOptions.truck) } : {}),
                ...(project.traffic ? { mode: undefined, truck: undefined } : {}),
              }),
            }
          : {}),
      });
    }
  };

  if (!project) return null;

  return (
    <>
      <Grid container direction="column">
        <Grid
          container
          direction="column"
          style={{
            padding: `11px 20px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
          wrap="nowrap"
        >
          <Grid container direction="row" wrap="nowrap">
            <HeaderContent
              titleIntlId="projects.settings.optimizer.title"
              article={{
                intlId: `projects.settings.optimizer.subtitle`,
                link: retrieveDoc({ supportPage: `settingsOptimizer`, language: user?.language.value }),
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
      >
        <Grid container direction="column" style={{ maxWidth: `450px` }}>
          <OptimizerConfigComponent
            asUser={asUser}
            onChange={(updatedData: OptimizerConfig) => {
              setOptimizerConfig({ ...updatedData.props });
            }}
            handleEnter={(updatedData: OptimizerConfig) => {
              setOptimizerConfig({ ...updatedData.props });
            }}
            optimizerConfig={OptimizerConfig.create(optimizerConfig)}
          />
        </Grid>

        <Grid container direction="column" style={{ maxWidth: `450px` }}>
          <TrafficOptionsComponent
            asUser={asUser}
            project={project}
            trafficOptions={trafficOptions}
            onChange={(updatedData) => {
              setTrafficOptions(updatedData);
            }}
          />
        </Grid>

        <Grid item style={{ position: `fixed`, bottom: `40px`, right: `40px` }}>
          <Button
            id="project-settings-optimizer-save-changes"
            variant={dirtyState ? `default` : `flat`}
            onClick={handleEditProject}
            style={{ maxWidth: `200px` }}
            intlId="settings.buttonupdate"
          />
        </Grid>
      </Grid>
    </>
  );
};
